import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { APIHOST } from '../../common';
import { Order } from '../../types/order';
import { formatDateFromYYYYMMDD } from '../../../lib/datetime';
import { AvailabilityCalendar} from '@/src/types/availability';
import { HotelChangeRequest, HotelRoomChange } from '../../types/hotelChange';
import { useApp } from '../AppContext';



interface HotelChangeProps {
  open: boolean;
  onClose: () => void;
  orderDocumentId: string;
}


export default function HotelChange({ open, onClose, orderDocumentId }: HotelChangeProps) {
  const [order, setOrder] = useState<Order | null>(null);
  const [availability, setAvailability] = useState<AvailabilityCalendar>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [selectedHotel, setSelectedHotel] = useState<string>('');
  const [roomAssignments, setRoomAssignments] = useState<Record<string, string>>({});
  const [swapHotels, setSwapHotels] = useState(false);

  const {setSnackbarInput} = useApp();

  useEffect(() => {
    if (open && orderDocumentId) {
      // Reset state when dialog opens
      setActiveStep(0);
      setSelectedHotel('');
      setRoomAssignments({});
      setSwapHotels(false);
      setError('');
      
      loadOrderData()
      .then((order) => order && loadAvailableRooms(order.conference_group.conference.documentId));
    }
  }, [open, orderDocumentId]);

  const loadOrderData = async (): Promise<Order | null> => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/orders/${orderDocumentId}?populate[]=conference_group.conference&populate[]=conference_hotel.hotel&populate[]=booker&populate[]=order_rooms&populate[]=order_rooms.conference_hotel_room&populate[]=order_rooms.order_room_occupants`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load order data');
      }

      const data = await response.json();
      const orderData = data.data as Order;
      setOrder(orderData);
      
      // Set the current hotel as selected by default
      if (orderData.conference_hotel) {
        setSelectedHotel(orderData.conference_hotel.documentId);
      }
      
      return orderData;
    } catch (error) {
      console.error('Error loading order:', error);
      setSnackbarInput({ message: 'Failed to load order data', severity: 'error' });
      setError('Failed to load order data');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableRooms = async (conferenceId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(
        `${APIHOST}/api/conferences/${conferenceId}/availability`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAvailability(data as AvailabilityCalendar);
      }
    } catch (error) {
      console.error('Error loading available rooms:', error);
      setSnackbarInput({ message: 'Failed to load available rooms', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Alert severity="error">{error}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }


  const availableRooms = Object.values(availability).sort((a, b) => {
    if (a.name && b.name) {
      return a.name.localeCompare(b.name);
    }
    return 0;
  });

  const selectedHotelData = availability[selectedHotel];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleHotelSelect = (hotelId: string) => {
    setSelectedHotel(hotelId);
    setRoomAssignments({}); // Reset room assignments when changing hotel
  };

  const handleSwapToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSwapHotels(event.target.checked);
    if (!event.target.checked) {
      // If switching back to "keep hotel", reset to current hotel
      if (order?.conference_hotel) {
        setSelectedHotel(order.conference_hotel.documentId);
      }
    } else {
      // If switching to "swap hotels", clear selection to force user to choose
      setSelectedHotel('');
    }
    setRoomAssignments({});
  };

  const handleProceedToRoomAssignment = () => {
    if (!swapHotels || selectedHotel) {
      setActiveStep(1);
    }
  };

  const handleRoomAssignment = (orderRoomId: string, newRoomId: string) => {
    setRoomAssignments(prev => ({
      ...prev,
      [orderRoomId]: newRoomId
    }));
  };

  const handleChangeHotel = async () => {
    if (!selectedHotel || !order || !order.order_rooms) {
      setError('Missing required data for hotel change');
      return;
    }

    // Convert room assignments to the required format
    const roomChanges: HotelRoomChange[] = order.order_rooms.map(room => ({
      oldRoomId: room.documentId || '',
      newRoomId: roomAssignments[room.documentId || ''] as string
    }));

    // Validate all rooms have been assigned
    if (roomChanges.some(change => !change.newRoomId)) {
      setError('Please assign all rooms before completing the hotel change');
      return;
    }

    const requestBody: HotelChangeRequest = {
      newHotelId: selectedHotel,
      roomChanges
    };

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/orders/${orderDocumentId}/hotelChange`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update hotel/rooms');
      }

      const result = await response.json();
      console.log('Hotel/Room change successful:', result);
      setSnackbarInput({ message: 'Hotel/Room change successful', severity: 'success' });
      onClose();
    } catch (error) {
      console.error('Error changing hotel:', error);
      setError(error instanceof Error ? error.message : 'Failed to change hotel/rooms');
      setSnackbarInput({ message: error instanceof Error ? error.message : 'Failed to change hotel/rooms', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
        <Typography variant="h6">Swap Hotel/Rooms - Order #{order?.confirmation}</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {order && 
          <Grid container spacing={3}>
            {/* Order Information */}
            <Grid size={{ xs: 12}}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'grid', gap: 1 }}>
                    <Typography variant="body2">
                      <strong>Booker:</strong> {order.booker?.firstName} {order.booker?.lastName}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Current Hotel:</strong> {order.conference_hotel?.hotel?.longName || 'No hotel selected'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Hotel Change Options - Only show on first step */}
            {activeStep === 0 && 
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Hotel Change Options
                  </Typography>
                  
                  {/* Switch for keep/swap hotel */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Typography variant="body2" sx={{ mr: 2 }}>
                      Keep Current Hotel
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={swapHotels}
                          onChange={handleSwapToggle}
                          color="primary"
                        />
                      }
                      label=""
                      sx={{ mx: 1 }}
                    />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      Swap Hotels
                    </Typography>
                  </Box>

                  {!swapHotels && (
                    <Typography variant="body2" color="text.secondary">
                      The current hotel will be kept for this order. You can swap rooms in this hotel by clicking next.
                    </Typography>
                  )}

                  {/* Show hotel options only when swap is selected */}
                  {swapHotels && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Select a new hotel from the available options. You will then be able to select rooms for the new hotel on the next step.
                      </Typography>
                      {availableRooms.length > 0 ? (
                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', mt: 2 }}>
                          {availableRooms.map((hotel) => {
                            const isCurrentHotel = hotel.documentId === order?.conference_hotel?.documentId;
                            const isSelected = selectedHotel === hotel.documentId;
                            
                            return (
                              <Card 
                                key={hotel.documentId} 
                                variant="outlined" 
                                sx={{ 
                                  border: isSelected ? '2px solid' : undefined,
                                  borderColor: isSelected ? 'primary.main' : undefined,
                                  opacity: isCurrentHotel ? 0.6 : 1
                                }}
                              >
                                <CardContent sx={{ p: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    {hotel.longName}
                                    {isCurrentHotel && (
                                      <Chip 
                                        label="Current" 
                                        size="small" 
                                        color="secondary"
                                        sx={{ ml: 1 }}
                                      />
                                    )}
                                  </Typography>
                                  {hotel.rooms && Object.values(hotel.rooms).length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                      <Box sx={{ mt: 1 }}>
                                        {Object.values(hotel.rooms).map((room: any) => (
                                          <Typography key={room.documentId} variant="caption" display="block" color="text.secondary">
                                            • {room.name} - ${room.dailyRate?.toFixed(2) || 'N/A'}
                                          </Typography>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}
                                  <Button
                                    variant={isSelected ? "contained" : "outlined"}
                                    fullWidth
                                    disabled={isCurrentHotel}
                                    onClick={() => handleHotelSelect(hotel.documentId)}
                                  >
                                    {isSelected ? 'Selected' : isCurrentHotel ? 'Current Hotel' : 'Select'}
                                  </Button>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No available hotels found for this conference
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>}

            
            {activeStep === 1 && 
              <Grid size={{ xs: 12 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Room Assignment
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Assign new rooms for each booking. Select the appropriate room from the new hotel for each existing booking in the order.
                    </Typography>
                    {order.order_rooms && order.order_rooms.length > 0 && selectedHotelData ? (
                      <Box sx={{ mt: 2 }}>
                        {order.order_rooms.map((room, index) => (
                          <Card key={room.documentId || index} sx={{ mb: 2 }} variant="outlined">
                            <CardContent>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 5 }}>
                                  <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                      Current
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                                      {order.conference_hotel?.hotel?.longName}
                                    </Typography>
                                    <Typography variant="body2">
                                      {room.conference_hotel_room?.name || 'Unknown Room'}
                                    </Typography>
                                    <Chip 
                                      label={room.type || 'Student'} 
                                      size="small" 
                                      sx={{ textTransform: 'capitalize', mt: 0.5 }}
                                    />
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                      {room.arrivalDate && room.departureDate && 
                                        `${formatDateFromYYYYMMDD(room.arrivalDate)} - ${formatDateFromYYYYMMDD(room.departureDate)}`
                                      }
                                    </Typography>
                                    {room.order_room_occupants && room.order_room_occupants.length > 0 && (
                                      <Box sx={{ mt: 1 }}>
                                        {room.order_room_occupants.map((occupant, idx) => (
                                          <Chip
                                            key={occupant.documentId || idx}
                                            label={`${occupant.firstName} ${occupant.lastName}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{ mr: 0.5, mb: 0.5 }}
                                          />
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, md: 1 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <Typography variant="body2" align="center">
                                      →
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                      New
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                      {selectedHotelData.longName}
                                    </Typography>
                                    <FormControl fullWidth>
                                      <InputLabel>Select New Room</InputLabel>
                                      <Select
                                        value={room.documentId ? (roomAssignments[room.documentId] || '') : ''}
                                        label="Select New Room"
                                        onChange={(e) => room.documentId && handleRoomAssignment(room.documentId, e.target.value)}
                                      >
                                        {selectedHotelData.rooms && Object.values(selectedHotelData.rooms).map((availableRoom: any) => (
                                          <MenuItem key={availableRoom.documentId} value={availableRoom.documentId}>
                                            {availableRoom.name} - ${availableRoom.dailyRate?.toFixed(2) || 'N/A'} 
                                            (Available: {Math.min(...Object.values(availableRoom.availability || {}).map((avail: any) => avail.available || 0))})
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        ))}
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                          <Button
                            onClick={handleBack}
                            sx={{ mr: 1 }}
                          >
                            Back
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleChangeHotel}
                            disabled={loading || order.order_rooms?.some(room => room.documentId === undefined || !roomAssignments[room.documentId])}
                          >
                            {loading ? 'Changing Hotel...' : 'Complete Hotel Change'}
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No rooms to assign or invalid hotel selection
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            }
          </Grid>
        }
      </DialogContent>
      <DialogActions>
        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={handleProceedToRoomAssignment}
            disabled={swapHotels && !selectedHotel}
          >
            Next
          </Button>
        )}
      </DialogActions>
      
    </Dialog>
  );
}